<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  phase: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    default: 0,
  },
});

const { t } = useI18n();

const PROCESS_GROUP_LABELS = {
  initiating: 'PROJECTS.PHASES.PROCESS_GROUPS.INITIATING',
  planning: 'PROJECTS.PHASES.PROCESS_GROUPS.PLANNING',
  executing: 'PROJECTS.PHASES.PROCESS_GROUPS.EXECUTING',
  monitoring: 'PROJECTS.PHASES.PROCESS_GROUPS.MONITORING',
  closing: 'PROJECTS.PHASES.PROCESS_GROUPS.CLOSING',
};

const GATE_CLASS = {
  pending: 'bg-n-amber-3 text-n-amber-11',
  approved: 'bg-n-teal-3 text-n-teal-11',
  rejected: 'bg-n-ruby-3 text-n-ruby-11',
};

const processGroupLabel = computed(() => {
  const key = PROCESS_GROUP_LABELS[props.phase.process_group];
  return key ? t(key) : props.phase.process_group || '';
});

const isGate = computed(() => Boolean(props.phase.is_gate));
const gateStatus = computed(() => props.phase.gate_status || 'pending');
const gateClass = computed(
  () => GATE_CLASS[gateStatus.value] || GATE_CLASS.pending
);

// Sum stage task counts when the embedded phase exposes pipeline stages.
const stages = computed(() => props.phase.pipeline_stages || []);

const taskTotals = computed(() => {
  const stageList = stages.value;
  if (!stageList.length) {
    return {
      total: props.phase.tasks_count || 0,
      done: props.phase.completed_tasks_count || 0,
    };
  }
  return stageList.reduce(
    (acc, stage) => ({
      total: acc.total + (stage.tasks_count || 0),
      done: acc.done + (stage.completed_tasks_count || 0),
    }),
    { total: 0, done: 0 }
  );
});
</script>

<template>
  <div class="flex items-center gap-3 px-2 py-2.5 group">
    <!-- Order badge -->
    <span
      class="flex items-center justify-center text-xs font-medium rounded-md size-6 shrink-0 bg-n-alpha-1 text-n-slate-11"
    >
      {{ index + 1 }}
    </span>

    <!-- Name + process group -->
    <div class="flex flex-col min-w-0 flex-1">
      <span
        class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
      >
        <span class="truncate">{{ phase.name }}</span>
        <span
          v-if="isGate"
          v-tooltip.top="t('PROJECTS.PHASES.GATE')"
          class="i-lucide-flag-triangle-right size-3.5 text-n-amber-11 shrink-0"
        />
      </span>
      <span v-if="processGroupLabel" class="text-xs truncate text-n-slate-10">
        {{ processGroupLabel }}
      </span>
    </div>

    <!-- Task count summary -->
    <span
      class="hidden sm:flex items-center gap-1 px-2 py-0.5 text-xs rounded-md shrink-0 bg-n-alpha-1 text-n-slate-11"
      :title="t('PROJECTS.PHASES.TASKS_SUMMARY')"
    >
      <span class="i-lucide-check-square size-3.5" />
      {{ taskTotals.done }}/{{ taskTotals.total }}
    </span>

    <!-- Gate status -->
    <span
      v-if="isGate"
      class="inline-flex items-center px-2 py-0.5 text-xs rounded-md shrink-0"
      :class="gateClass"
    >
      {{ t(`PROJECTS.PHASES.GATE_STATUS.${gateStatus.toUpperCase()}`) }}
    </span>

    <!-- Status -->
    <span class="text-xs shrink-0 w-20 text-right text-n-slate-10">
      {{ phase.status || '' }}
    </span>
  </div>
</template>
